import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowUpRight, BookOpen, Award, Sparkles, FlaskConical, Stethoscope, GraduationCap, Microscope, Activity, CircuitBoard } from "lucide-react";

export function Overline({ children, className = "" }) {
  return <div className={`mono text-[11px] uppercase tracking-[0.22em] text-prestige ${className}`}>{children}</div>;
}

export function GoldRule({ className = "" }) {
  return <div className={`h-px w-10 bg-prestige ${className}`} />;
}

export function MedicallyReviewed({ by = "Dr. Tania Gurdip Singh", on }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-prestige/40 bg-prestige-bg px-3 py-1 text-xs text-navy">
      <ShieldCheck size={13} strokeWidth={1.5} className="text-prestige"/>
      <span className="mono tracking-wide">Medically reviewed by {by}{on ? ` · ${on}` : ""}</span>
    </div>
  );
}

export function CredentialBadge({ label, icon: Icon = ShieldCheck }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 border border-prestige/40 rounded-full bg-prestige-bg/50" data-testid={`credential-${label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
      <Icon size={14} strokeWidth={1.5} className="text-prestige" />
      <span className="text-xs text-navy font-medium tracking-wide">{label}</span>
    </div>
  );
}

const iconMap = { ShieldCheck, BookOpen, Award, Sparkles, FlaskConical, Stethoscope, GraduationCap, Microscope, Activity, CircuitBoard };

export function TrustRibbon({ credentials }) {
  if (!credentials?.length) return null;
  return (
    <section aria-label="Credentials" data-testid="trust-ribbon" className="bg-prestige-bg border-y border-prestige/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 lg:px-12 py-7 md:py-8">
        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 md:gap-x-10 gap-y-6 md:gap-y-7 items-start justify-items-start"
          role="list"
        >
          {credentials.map((c) => {
            const I = iconMap[c.icon] || ShieldCheck;
            const slug = c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            return (
              <li
                key={c.label}
                data-testid={`credential-${slug}`}
                className="flex items-start gap-3 min-w-0 w-full"
              >
                <I size={18} strokeWidth={1.5} className="text-prestige shrink-0 mt-[3px]" aria-hidden="true" />
                <div className="min-w-0">
                  <div className="text-[13.5px] leading-snug text-navy font-medium tracking-[0.005em] break-words">
                    {c.label}
                  </div>
                  {c.sub && (
                    <div className="text-[11.5px] leading-snug text-stone2 font-normal tracking-[0.01em] mt-1 break-words">
                      {c.sub}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`py-16 md:py-24 ${className}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">{children}</div>
    </section>
  );
}

export function CardLink({ to, overline, title, description, className = "", testid }) {
  return (
    <Link to={to} data-testid={testid} className={`group block bg-white border border-line p-6 md:p-8 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 ${className}`}>
      {overline && <Overline>{overline}</Overline>}
      <h3 className="font-serif text-2xl md:text-3xl text-navy mt-2 leading-tight">{title}</h3>
      {description && <p className="text-sm text-stone2 mt-3 leading-relaxed">{description}</p>}
      <div className="mt-5 flex items-center gap-1.5 text-navy text-xs mono uppercase tracking-widest opacity-70 group-hover:opacity-100 transition-opacity">
        Read <ArrowUpRight size={14} strokeWidth={1.5}/>
      </div>
    </Link>
  );
}

export function PrimaryButton({ to, href, children, testid, className = "", onClick, type }) {
  const cls = `inline-flex items-center justify-center gap-2 bg-prestige text-navy text-sm font-medium px-6 py-3 rounded-full hover:bg-prestige-soft transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-prestige focus-visible:ring-offset-2 ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
  if (href) return <a href={href} data-testid={testid} className={cls}>{children}</a>;
  return <button data-testid={testid} onClick={onClick} type={type} className={cls}>{children}</button>;
}

export function OutlineButton({ to, href, children, testid, className = "" }) {
  const cls = `inline-flex items-center justify-center gap-2 border border-navy text-navy text-sm font-medium px-6 py-3 rounded-full hover:bg-navy hover:text-white transition-colors ${className}`;
  if (to) return <Link to={to} data-testid={testid} className={cls}>{children}</Link>;
  if (href) return <a href={href} data-testid={testid} className={cls}>{children}</a>;
  return <span className={cls}>{children}</span>;
}

export function EntityChip({ label, testid }) {
  return <span data-testid={testid} className="inline-block rounded-full px-3 py-1 text-xs mono bg-clinical text-navy hover:bg-line transition-colors">{label}</span>;
}
